import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import { AvailableCharitiesModal, DonateToCharityModal } from '@hero/components/modals';
import { AUTOMATION_CREATION_STEPS } from '@hero/constants';
import { useAutomationCreationHeader, useGetCharity } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { ProductSelectionSection, WidgetFooter } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import { useUpdateAutomation } from '@hero/services';
import { useNewAutomationStore, useValidateRules } from '@hero/store/hooks';
import { getProductsRule } from '@hero/validations';

import * as S from './style';

function NewAutomationGifts() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { charityEnabled, isCharityExist, anyCharityItemExists } = useGetCharity();

  const [isPageLoading, setIsPageLoading] = React.useState(false);
  const [showCharityInfoModal, setShowCharityInfoModal] = React.useState(false);
  const [showSelectCharitiesModal, setShowSelectCharitiesModal] = React.useState(false);

  const header = useAutomationCreationHeader({
    step: 1,
    setIsPageLoading
  });

  const { code, products, updateProducts, reset } = useNewAutomationStore();
  const { mutateAsync: updateAutomation, isLoading: isLoadingUpdateAutomation } =
    useUpdateAutomation();

  const onClickBackButton = () => {
    reset();
    navigate(ROUTE_PATHS.MY_AUTOMATION_LIST);
  };

  const { validate } = useValidateRules();

  const selectedSkus = useMemo(() => {
    return products.reduce((r, p) => r.concat(p.skus), []);
  }, [products]);

  const onNextButtonClick = async ({ skipCharity }) => {
    const isValid = await validate([{ rule: getProductsRule(), value: products }]);
    if (!isValid) return;

    const hasCharity = anyCharityItemExists(products);
    if (!hasCharity && charityEnabled && !skipCharity && isCharityExist)
      return setShowCharityInfoModal(true);

    setIsPageLoading(true);
    try {
      await updateAutomation({
        params: {
          code
        },
        body: {
          products,
          meta: {
            creationStep: AUTOMATION_CREATION_STEPS.newAutomationRules
          }
        }
      });

      navigate(ROUTE_PATHS.NEW_AUTOMATION_RULES);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (!code) {
      navigate(ROUTE_PATHS.MY_AUTOMATION_LIST);
    }
  }, []);

  return (
    <PageLayout
      header={header}
      navigation={{
        rightButton: {
          children: t('next'),
          onClick: onNextButtonClick,
          icon: isLoadingUpdateAutomation
        },
        leftButton: {
          children: t('back'),
          onClick: onClickBackButton
        }
      }}
    >
      <S.HeadTitle variant="subtitle4Bold">{t('giftListHeaderSubTitle')}</S.HeadTitle>
      <ProductSelectionSection
        selectedProducts={products}
        onChangeSelectedProductList={selectedProducts => {
          updateProducts(selectedProducts);
        }}
        components={{
          WidgetFooter: () => (
            <WidgetFooter
              products={products}
              selectedSkus={selectedSkus}
              onNextButtonClick={onNextButtonClick}
              isLoading={isPageLoading}
            />
          )
        }}
        featureType="automation"
        localShoppingCartEnabled
        isDraft
      />
      {isPageLoading && <AppLoader />}
      {showCharityInfoModal && (
        <DonateToCharityModal
          isOpen={showCharityInfoModal}
          setIsOpen={setShowCharityInfoModal}
          primaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setShowSelectCharitiesModal(true);
            },
            children: t('yes')
          }}
          secondaryButtonProps={{
            onClick: () => setShowCharityInfoModal(false),
            children: t('skip')
          }}
          descriptionTextKey="donateToCharityDesc2"
          titleKey="donateToCharityTitle2"
        />
      )}
      {showCharityInfoModal && (
        <DonateToCharityModal
          isOpen={showCharityInfoModal}
          setIsOpen={setShowCharityInfoModal}
          primaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              setShowSelectCharitiesModal(true);
            },
            children: t('yes')
          }}
          secondaryButtonProps={{
            onClick: () => {
              setShowCharityInfoModal(false);
              onNextButtonClick({ skipCharity: true });
            },
            children: t('skip')
          }}
          descriptionTextKey="donateToCharityDesc2"
          titleKey="donateToCharityTitle2"
        />
      )}
      {showSelectCharitiesModal && (
        <AvailableCharitiesModal
          isOpen={showSelectCharitiesModal}
          setIsOpen={setShowSelectCharitiesModal}
          setSelectedCharities={selectedCharities =>
            updateProducts([...products, ...selectedCharities])
          }
        />
      )}
    </PageLayout>
  );
}

export { NewAutomationGifts };
